//* Import necessary modules
import React, { useState, useEffect } from "react";

//* Import necessary components
import { Button, Badge, Spinner, Alert, Modal, Form } from "react-bootstrap";

//* Import icons
import { Close } from "@material-ui/icons";

//* Import necessary modules
import Select, {
  MultiValue,
  ActionMeta,
  StylesConfig,
  components,
  OptionProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  GroupBase,
} from "react-select";

//! Import custom hooks
import { useTags } from "../../hooks/tags/useTags";

//* Import hooks from react-query
import { useQueryClient } from "@tanstack/react-query";


//? Define the type of the props
type TagsSelectorProps = {
  selectedTagIds: string[];
  onChange: (selectedTagIds: string[]) => void;
  hideCreateTagButton?: boolean;
}

//? Define the type of the options
type OptionType = {
  value: string;
  label: string;
  color: string;
}

/**
 * Determines the appropriate text color (black or white) based on the provided background color to ensure optimal readability.
 *
 * @param hexColor - The background color in hexadecimal format (e.g., '#FFFFFF').
 * @returns A string representing the text color, either '#000' for black or '#fff' for white.
 */
function getTextColorForBackground(hexColor: string): string {
  const color = hexColor.replace("#", "");
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // YIQ Luma
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? "#000" : "#fff";
}

/**
 * CustomOption is a customized option component for the select input.
 *
 * @param props - Props extending OptionProps with OptionType, supporting multi-selection and grouping.
 * @returns A styled option element displaying a colored dot and label.
 */
const CustomOption: React.FC<
  OptionProps<OptionType, true, GroupBase<OptionType>>
> = (props) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span
          style={{
            display: "inline-block",
            width: "12px",
            height: "12px",
            backgroundColor: props.data.color,
            borderRadius: "50%",
            marginRight: "8px",
          }}
        ></span>
        {props.label}
      </div>
    </components.Option>
  );
};

/**
 * CustomMultiValueLabel is a React functional component that renders a label with a colored indicator.
 *
 * @param props - The properties passed to the component.
 * @param props.data.color - The background color for the indicator circle.
 * @param props.data.label - The text label to display next to the colored indicator.
 */
const CustomMultiValueLabel: React.FC<
  MultiValueGenericProps<OptionType, true, GroupBase<OptionType>>
> = (props) => {
  const textColor = getTextColorForBackground(props.data.color);
  return (
    <components.MultiValueLabel {...props}>
      <span
        style={{
          display: "inline-block",
          width: "10px",
          height: "10px",
          backgroundColor: props.data.color,
          borderRadius: "50%",
          marginRight: "4px",
        }}
      ></span>
      <span style={{ color: textColor }}>{props.data.label}</span>
    </components.MultiValueLabel>
  );
};

/**
 * Renders a custom multi-value remove component for the TagsSelector.
 *
 * @param props - The properties for the multi-value remove component.
 * @returns A React element representing the custom remove component.
 */
const CustomMultiValueRemove: React.FC<
  MultiValueRemoveProps<OptionType, true, GroupBase<OptionType>>
> = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <Close
        style={{
          cursor: "pointer",
          width: "16px",
          height: "16px",
          color: "#fff",
        }}
      />
    </components.MultiValueRemove>
  );
};

/**
 * A React functional component that allows users to select and manage tags.
 *
 * @param selectedTagIds - An array of tag IDs that are initially selected.
 * @param onChange - Callback function invoked when the selected tags change. Receives the updated array of selected tag IDs.
 * @param hideCreateTagButton - A boolean flag to determine whether the "Create Tag Quickly" button should be hidden.
 */
export const TagsSelector: React.FC<TagsSelectorProps> = ({
  selectedTagIds,
  onChange,
  hideCreateTagButton,
}) => {
  const token = localStorage.getItem("token") || "";

  const {
    tags,
    createTag,
    loading: tagsLoading,
    error: tagsError,
  } = useTags(token);

  const [localSelectedTagIds, setLocalSelectedTagIds] =
    useState<string[]>(selectedTagIds);

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newTagName, setNewTagName] = useState<string>("");

  const queryClient = useQueryClient();

  useEffect(() => {
    setLocalSelectedTagIds(selectedTagIds);
  }, [selectedTagIds]);

  const handleSelectChange = (
    selectedOptions: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    const selectedIds = selectedOptions.map((option) => option.value);
    setLocalSelectedTagIds(selectedIds);
    onChange(selectedIds);
  };

  const handleRemoveTag = (tagId: string) => {
    const updatedTags = localSelectedTagIds.filter((id) => id !== tagId);
    setLocalSelectedTagIds(updatedTags);
    onChange(updatedTags);
  };

  const handleOpenCreateModal = () => {
    setNewTagName("");
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleConfirmCreateTag = async () => {
    if (!newTagName.trim()) return;

    const randomColor =
      "#" +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0");

    try {
      await createTag({
        name: newTagName.trim(),
        color: randomColor,
      });
      queryClient.invalidateQueries({ queryKey: ["tags"] });

      // Selected the newly created tag
      setTimeout(() => {
        const newTag = tags.find((tag) => tag.name === newTagName.trim());
        if (newTag) {
          const updated = [...localSelectedTagIds, newTag.id];
          setLocalSelectedTagIds(updated);
          onChange(updated);
        }
      }, 500);
    } catch (err) {
      console.error("Falha ao criar tag rapidamente:", err);
    } finally {
      handleCloseCreateModal();
    }
  };

  const options: OptionType[] = tags.map((tag) => ({
    value: tag.id,
    label: tag.name,
    color: tag.color,
  }));

  const selectedOptions = options.filter((option) =>
    localSelectedTagIds.includes(option.value)
  );

  /**
   * Custom styles configuration for the TagsSelector component.
   *
   * @type {StylesConfig<OptionType, true, GroupBase<OptionType>>}
   * @property {Function} control - Styles for the control element.
   * @property {Function} multiValue - Styles for the selected multi-value items.
   * @property {Function} multiValueLabel - Styles for the label of selected multi-value items.
   * @property {Function} multiValueRemove - Styles for the remove button of selected multi-value items.
   * @property {Function} option - Styles for the options in the dropdown.
   * @property {Function} placeholder - Styles for the placeholder text.
   * @property {Function} noOptionsMessage - Styles for the no options message.
   */
  const customStyles: StylesConfig<OptionType, true, GroupBase<OptionType>> = {
    control: (styles) => ({
      ...styles,
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#adb5bd",
      },
    }),
    multiValue: (styles, { data }) => {
      const textColor = getTextColorForBackground(data.color);
      return {
        ...styles,
        backgroundColor: data.color || "#6c757d",
        color: textColor,
      };
    },
    multiValueLabel: (styles, { data }) => {
      const textColor = getTextColorForBackground(data.color);
      return {
        ...styles,
        color: textColor,
      };
    },
    multiValueRemove: (styles) => ({
      ...styles,
      color: "#fff",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        color: "#fff",
      },
    }),
    option: (styles, { data, isFocused, isSelected }) => {
      const textColor = getTextColorForBackground(data.color);
      return {
        ...styles,
        backgroundColor: isSelected
          ? data.color
          : isFocused
          ? "#f1f1f1"
          : "#fff",
        color: isSelected ? textColor : "#000",
        cursor: "pointer",
        ":active": {
          backgroundColor: data.color,
          color: textColor,
        },
      };
    },
    placeholder: (styles) => ({
      ...styles,
      color: "#6c757d",
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: "#6c757d",
    }),
  };

  return (
    <div>
      {tagsError && <Alert variant="danger">{tagsError}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <label htmlFor="tags-select" className="form-label">
          Tags
        </label>
        {!hideCreateTagButton && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleOpenCreateModal}
            disabled={tagsLoading}
          >
            Criar Tag Rapidamente
          </Button>
        )}
      </div>
      <Select
        id="tags-select"
        isMulti
        isLoading={tagsLoading}
        options={options}
        value={selectedOptions}
        onChange={handleSelectChange}
        styles={customStyles}
        placeholder={
          tagsLoading ? "Carregando tags..." : "Selecione as tags..."
        }
        noOptionsMessage={() =>
          tagsLoading ? "Carregando..." : "Nenhuma tag encontrada"
        }
        components={{
          Option: CustomOption,
          MultiValueLabel: CustomMultiValueLabel,
          MultiValueRemove: CustomMultiValueRemove,
        }}
      />

      <div
        className="mt-3"
        style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
      >
        {tags
          .filter((t) => localSelectedTagIds.includes(t.id))
          .map((tag) => {
            const textColor = getTextColorForBackground(tag.color);
            return (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.color || "#6c757d",
                  color: textColor,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  padding: "0.5em 0.75em",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    backgroundColor: tag.color,
                    borderRadius: "50%",
                    marginRight: "6px",
                  }}
                ></span>
                {tag.name}
                <Close
                  style={{
                    cursor: "pointer",
                    marginLeft: "8px",
                    width: "16px",
                    height: "16px",
                    color: textColor,
                  }}
                  onClick={() => handleRemoveTag(tag.id)}
                />
              </Badge>
            );
          })}
      </div>

      {tagsLoading && (
        <div className="d-flex justify-content-center align-items-center mt-3">
          <Spinner animation="border" variant="primary" role="status" />
        </div>
      )}

      {/* Modal para criação de tags */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
        <Modal.Header closeButton>
          <Modal.Title>Criar Nova Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="formTagName">
            <Form.Label>Nome da Tag</Form.Label>
            <Form.Control
              type="text"
              placeholder="Digite o nome da tag"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmCreateTag}>
            Criar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
